import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tv, Maximize } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { WheelBody } from "@/components/ProductWheel";

function CodeEntry() {
  const nav = useNavigate();
  const [v, setV] = useState("");
  const [manual, setManual] = useState(false);
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    let tok = null;
    const make = () => api.post("/tv/pair").then((r) => { tok = r.data.token; if (alive) setP(r.data); }).catch(() => {});
    make();
    const id = setInterval(() => {
      if (!tok) return;
      api.get(`/tv/pair/${tok}`).then((r) => { if (alive && r.data.session_code) nav(`/tv/${r.data.session_code}`); }).catch((e) => { if (e?.response?.status === 404) make(); });
    }, 2000);
    return () => { alive = false; clearInterval(id); };
  }, [nav]);
  const go = () => { const c = v.trim().toUpperCase(); if (c.length === 6) nav(`/tv/${c}`); };
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-[4vh] text-center" data-testid="tv-code-entry">
      <h1 className="text-[6vh] font-extrabold leading-none mb-[2vh] flex items-center gap-4"><Tv className="w-[7vh] h-[7vh] text-emerald-400" />Shfaq në TV</h1>
      {!manual ? (
        <>
          <p className="text-[2.6vh] text-white/70 mb-[3vh] max-w-3xl">Skano QR-në me telefon ose te telefoni shtyp ikonën e TV-së dhe shkruaj kodin.</p>
          <div className="flex items-center gap-[6vh]">
            <div className="bg-white p-[2vh] rounded-3xl" data-testid="tv-pair-qr">{p ? <QRCodeSVG value={`${window.location.origin}/lidh-tv/${p.code}`} size={Math.round(window.innerHeight * 0.36)} /> : <div style={{ width: "36vh", height: "36vh" }} />}</div>
            <div>
              <div className="text-[2.4vh] text-white/60 mb-2">Kodi i TV-së</div>
              <div data-testid="tv-pair-code" className="text-[10vh] font-black tracking-[0.15em] text-emerald-400 leading-none">{p ? p.code : "…"}</div>
            </div>
          </div>
          <button data-testid="tv-manual-toggle" onClick={() => setManual(true)} className="mt-[5vh] text-[2.4vh] text-white/60 underline">Kam kod nga telefoni</button>
        </>
      ) : (
        <>
          <p className="text-[2.6vh] text-white/70 mb-[3vh]">Shkruaj kodin që shfaqet në telefon.</p>
          <input data-testid="tv-code-input" autoFocus value={v} maxLength={6} onChange={(e) => setV(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} onKeyDown={(e) => e.key === "Enter" && go()}
            className="w-80 h-20 text-center text-5xl font-black tracking-[0.3em] rounded-2xl bg-white/10 border-2 border-emerald-400 outline-none" placeholder="------" />
          <button data-testid="tv-code-go" onClick={go} className="btn-3d-green mt-6 h-14 px-10 rounded-2xl text-xl font-bold">Lidhu</button>
          <button onClick={() => setManual(false)} className="mt-6 text-white/60 underline">Shfaq QR-në</button>
        </>
      )}
    </div>
  );
}

function Screen({ code }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/tv/screen/${code}`).then((r) => { if (alive) { setD(r.data); setErr(false); } }).catch(() => { if (alive) setErr(true); });
    load();
    const id = setInterval(load, 2500);
    return () => { alive = false; clearInterval(id); };
  }, [code]);
  const full = () => { const el = document.documentElement; if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {}); };
  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-8 text-center" data-testid="tv-ended">
      <Tv className="w-20 h-20 text-white/40 mb-6" />
      <h1 className="text-4xl font-extrabold mb-3">Transmetimi përfundoi</h1>
      <p className="text-lg text-white/60">Kodi {code} nuk është më aktiv. Krijo një kod të ri nga telefoni.</p>
      <a href="/tv" className="mt-8 text-emerald-400 text-xl font-bold">Shkruaj kod tjetër</a>
    </div>
  );
  if (!d) return <div className="min-h-screen bg-slate-950" data-testid="tv-loading" />;
  const w = d.wheels[Math.min(d.wheel, d.wheels.length - 1)];
  return (
    <div className="h-screen bg-slate-950 flex flex-col justify-center overflow-hidden cursor-none relative" onClick={full} data-testid="tv-screen">
      <div className="absolute top-[3vh] left-[3vw] flex items-center gap-4 z-20">
        {d.logo && <img src={d.logo} alt="" className="h-[8vh] w-[8vh] rounded-2xl object-cover bg-white" />}
        <div className="text-white/80 text-[3vh] font-bold" data-testid="tv-title">{d.title}</div>
      </div>
      {!document.fullscreenElement && <button onClick={full} data-testid="tv-fullscreen" className="absolute top-6 right-8 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center cursor-pointer"><Maximize className="w-6 h-6" /></button>}
      {w ? <WheelBody key={`${d.wheel}-${w.title}-${w.items.length}`} big tv list={w.items} title={w.title} paused={!d.playing} /> : <div className="text-white text-center text-3xl">Nuk ka produkte</div>}
      {!d.playing && <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xl font-bold px-5 py-2 rounded-full" data-testid="tv-paused">Në pauzë</div>}
      <div className="absolute bottom-6 right-8 text-white/30 text-lg font-bold">GjejShqip.com</div>
    </div>
  );
}

export default function TvPage() {
  const { code } = useParams();
  return code ? <Screen code={code.toUpperCase()} /> : <CodeEntry />;
}
